import { Component, input, OnChanges, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { iconMap } from 'src/app/project/shared/enums/icon-map';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';

export type Step = {
  index: number;
  emit?: (args?: unknown) => void;
  group: {
    id: string;
    label: string;
    values?: { id: string; label: string; hasChildrenStep?: boolean }[];
    hideChildrenValues?: boolean;
    valuesCallback?: (args?: unknown) => Observable<unknown>;
    labelCallback?: (args?: unknown) => Observable<unknown>;
  }[];
};
@Component({
  selector: 'pp-multi-step-dropdown',
  styleUrls: ['./multi-step-dropdown.component.scss'],
  templateUrl: './multi-step-dropdown.component.html',
  standalone: false,
})
export class MultiStepDropdownComponent implements OnInit, OnChanges {
  ppSteps = input<Step[]>([]);
  ppBackCallback = input<() => void>();
  ppIsLoading = input<boolean>(false);
  ppSelectedId = input<string>(null);
  ppSelectedParentId = input<string>(null);

  EIconPath = EIconPath;
  iconMap = iconMap;

  isLastStep: boolean;
  keyword = undefined;
  currentStep = 1;
  step: Step;
  filteredSteps: Step[];
  stepLoading: { [step: number]: boolean } = {};

  collapsedGroup: {
    [groupId: string]: boolean;
  } = {};

  ngOnInit() {
    this.currentStep = 1;
    this.step = this.ppSteps().find((step) => step.index === this.currentStep);
  }

  ngOnChanges() {
    const steps = this.ppSteps();
    const previousStep = this.currentStep;

    this.currentStep = steps.length;
    this.step = steps.find((step) => step.index === this.currentStep);

    if (this.currentStep !== previousStep) {
      this.keyword = '';
    }

    if (this.step) {
      this.isLastStep = this.step.index === steps.length && !this.ppIsLoading();
    }
  }

  setKeyword(keyword: string): void {
    if (this.ppIsLoading()) {
      return;
    }

    this.keyword = keyword.toLowerCase();
    this.filterSteps();
  }

  filterSteps(): void {
    if (this.keyword === '') {
      this.step = this.ppSteps().find((step) => step.index === this.currentStep);
      return;
    }

    const originalStep = this.ppSteps().find((step) => step.index === this.currentStep);

    this.step = {
      ...originalStep,
      group: originalStep?.group
        ?.map((group) => {
          const groupMatches = group.label.toLowerCase().includes(this.keyword);

          if (groupMatches) {
            return group;
          }

          const filteredValues = group.values?.filter((value) =>
            value.label.toLowerCase().includes(this.keyword),
          );

          if (filteredValues?.length) {
            return { ...group, values: filteredValues };
          }

          return null;
        })
        .filter(Boolean),
    };
  }

  toggleGroup(group: Step['group'][0]): void {
    if (this.ppIsLoading()) {
      return;
    }

    if (group.values?.length) {
      if (this.isLastStep && this.currentStep !== 1) {
        return;
      }

      if (group.hideChildrenValues) {
        // No need to collapse groups if we are hiding children values

        if (group?.labelCallback) {
          const loadingStep = this.currentStep;
          this.stepLoading[loadingStep] = true;

          group.labelCallback(group).subscribe((response) => {
            this.stepLoading[loadingStep] = false;
          });
        }

        return;
      } else {
        this.handleCollapseGroups(group.id);
      }
    } else {
      if (this.isLastStep && this.currentStep === 1) {
        this.handleSelection(group.id);
        return;
      }

      this.currentStep = this.currentStep + 1;
    }
  }

  nextStep(groupId: string, valueId: string): void {
    if (this.ppIsLoading()) {
      return;
    }

    const currentGroup = this.step.group.find((group) => group.id === groupId);
    const currentValue = currentGroup?.values?.find((value) => value.id === valueId);

    if (currentGroup?.valuesCallback) {
      const loadingStep = this.currentStep;
      this.stepLoading[loadingStep] = true;
      currentGroup.valuesCallback(valueId).subscribe((response) => {
        this.stepLoading[loadingStep] = false;
      });
    }

    if (this.isLastStep && valueId && !currentValue?.hasChildrenStep) {
      this.handleSelection(valueId);
    }
  }

  handleCollapseGroups(groupId: string): void {
    if (!this.collapsedGroup[groupId]) {
      this.collapsedGroup[groupId] = true;

      return;
    }

    this.collapsedGroup[groupId] = !this.collapsedGroup[groupId];
  }

  handleSelection(valueId: string): void {
    if (this.step.group.length === 1) {
      this.step.emit(valueId);
    }
  }

  back(): void {
    this.currentStep = this.currentStep - 1;
    this.step = this.ppSteps().find((step) => step.index === this.currentStep);

    if (this.ppBackCallback) {
      this.ppBackCallback();
    }
  }
}
